import React, { useEffect, useContext } from "react";
import styled from "styled-components";
import axios from "axios";
import Table from "rc-table";
import Paginate from "react-paginate";

import columns from "./TableColumns";
import GlobalContext from "../../../Hooks/globalContext";
import { HOST, ADMIN_PAGE_UNIT } from "../../../Utils/constants";
import useState from "../../../Hooks/useState";
import Loader from "../../Common/Loader";
import useInput from "../../../Hooks/useInput";

const PageWrapper = styled.div`
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const FilterSelect = styled.select`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 14px;
  color: #f1f5f9;
  font-size: 13px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  &:focus {
    border-color: #3d73ad;
  }
  option {
    background: #1e293b;
    color: #f1f5f9;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 14px;
  color: #f1f5f9;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    border-color: #3d73ad;
    box-shadow: 0 0 0 3px rgba(61, 115, 173, 0.15);
  }
`;

const SearchButton = styled.button`
  background: #3d73ad;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover {
    background: #2a5a8f;
  }
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  backdrop-filter: blur(12px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TableWrapper = styled.div`
  overflow: auto;
  flex: 1;

  .rc-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }
  .rc-table-thead .rc-table-cell {
    padding: 12px 20px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(15, 23, 42, 0.4);
  }
  .rc-table-tbody .rc-table-cell {
    padding: 14px 20px;
    color: #e2e8f0;
    font-size: 13px;
    white-space: nowrap;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }
  .rc-table-tbody .rc-table-row:hover {
    background: rgba(61, 115, 173, 0.06);
  }
  .rc-table-placeholder {
    display: none;
  }
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
  color: #64748b;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #475569;
  font-size: 14px;
`;

const Page = ({ adminPage, hostPath }) => {
  const table = useState(null);
  const page = useState(null);
  const nowPage = useState(0);
  const token = useState(null);
  const { httpHeader } = useContext(GlobalContext);
  const userAmount = useState(null);
  const searchInput = useInput({
    defaultValue: "",
    maxLength: 50,
  });
  const searchType = useState("id");

  const preload = () => {
    tableFactory({ selected: 0, init: true });
  };

  const tableFactory = async ({ selected, init = false, isSearch = false }) => {
    nowPage.setState(selected);

    const { data: tokens } = await axios.post(`${HOST}/api/tokens`, null, httpHeader());
    token.setState(tokens);
    page.setState({ ...page.state, loading: true });
    const requestBody = {
      perPage: ADMIN_PAGE_UNIT,
      init,
      selected: selected + 1,
      search: isSearch && searchInput.value,
      searchType: isSearch && searchType.state,
    };
    const { data } = await axios.post(`${HOST}${hostPath}`, requestBody, httpHeader());
    table.setState(data.select);
    userAmount.setState(data.count);
    page.setState({
      ...page.state,
      loading: false,
      pageCount: Math.ceil(data.count / ADMIN_PAGE_UNIT),
    });
  };

  const selectOnChange = ({ target: { value } }) => {
    searchType.setState(value);
  };

  const searchOnSubmit = (e) => {
    e.preventDefault();
    tableFactory({ selected: 0, isSearch: true });
  };

  const column = columns({ page: adminPage, reset: tableFactory, nowPage, token: token.state });

  useEffect(preload, []);

  const tableData = table.state || [];

  return (
    <PageWrapper>
      <FilterBar>
        <FilterSelect onChange={selectOnChange}>
          <option value="id">ID로 검색</option>
          <option value="email">email로 검색</option>
          <option value="wallet_address">지갑주소로 검색</option>
          <option value="referral_code">추천인코드로 검색</option>
          <option value="recommend">추천인으로 검색</option>
        </FilterSelect>
        <form
          onSubmit={searchOnSubmit}
          style={{ display: "flex", gap: "8px", flex: 1, maxWidth: "400px" }}
        >
          <SearchInput placeholder="검색어 입력..." {...searchInput} />
          <SearchButton type="submit">검색</SearchButton>
        </form>
      </FilterBar>
      <GlassCard>
        <TableWrapper>
          {tableData.length > 0 ? (
            <Table rowKey={({ id }) => id} columns={column.user} data={tableData} />
          ) : (
            <EmptyState>
              데이터가 없습니다
            </EmptyState>
          )}
        </TableWrapper>
        <TableFooter>
          <span>총 유저 수 {userAmount.state || 0}</span>
          <div>
            {page.state && page.state.pageCount > 0 && (
              <Paginate
                previousLabel={"<"}
                nextLabel={">"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={page.state.pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={5}
                onPageChange={tableFactory}
                containerClassName={"pagination"}
                subContainerClassName={"pages pagination"}
                activeClassName={"active"}
              />
            )}
          </div>
        </TableFooter>
      </GlassCard>
    </PageWrapper>
  );
};

export default Page;
